import React, { useEffect, useRef, useState } from "react";
import { RING, RING_MAX_MS, LISTEN_RETRY_MS, LISTEN_WAIT_MS, listenRefusal, roleOf, farLegOf, labelFor, headline, firstName } from "./liveListen.js";

/**
 * A call to an AI persona, heard live through this browser. See liveListen.js for the rules; this
 * is the browser half: the ring, the room, the speakers.
 *
 * ⛔ THE SPEAKERS ARE UNLOCKED BY THE CALL CLICK, NOT HERE. A browser lets a page make sound only
 * after a click, and this panel mounts AFTER the click's handler has awaited the platform. So the
 * Call button calls `primeAudio()` first thing, before any await, and everything here plays
 * through that unlocked context. If the browser still holds inbound audio back, the panel says so
 * and offers the click it needs (the dashboard's Rep Phone learned this on 2026-09-03: "we are
 * arming the microphone, but we also need to arm the speakers").
 *
 * ⛔ IT CAN NEVER BE HEARD. The pass the platform mints cannot publish, and nothing here asks for
 * a microphone.
 */

let audioCtx = null;

/** Call from the Call button's click handler, before any await. Safe to call anywhere, any time. */
export function primeAudio() {
  try {
    if (typeof window === "undefined") return null;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!audioCtx) audioCtx = new AC();
    if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
    return audioCtx;
  } catch {
    return null;
  }
}

/** Ring until the returned function is called. */
function startRing() {
  const ctx = primeAudio();
  if (!ctx) return () => {};
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(ctx.destination);
  const oscs = RING.freqs.map((f) => {
    const o = ctx.createOscillator();
    o.frequency.value = f;
    o.connect(gain);
    o.start();
    return o;
  });
  let timer = null;
  let stopped = false;
  const ring = () => {
    if (stopped) return;
    const t = ctx.currentTime;
    const on = RING.onMs / 1000;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(RING.gain, t + 0.03);   // no click at the edges
    gain.gain.setValueAtTime(RING.gain, t + on - 0.03);
    gain.gain.linearRampToValueAtTime(0, t + on);
    timer = setTimeout(ring, RING.onMs + RING.offMs);
  };
  ring();
  return () => {
    stopped = true;
    clearTimeout(timer);
    try {
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      oscs.forEach((o) => o.stop());
      gain.disconnect();
    } catch { /* already stopped */ }
  };
}

/**
 * `callId` is this app's call row (null while the platform is still being asked: it rings anyway);
 * `listen(callId)` resolves to { url, token } or throws the API error. `initial` lets the render
 * test draw a state without a browser.
 */
export default function LiveListen({ T, mono, callId, name, listen, initial = null }) {
  const [state, setState] = useState(initial?.state || "connecting");   // connecting | ringing | live | ended | failed
  const [speaking, setSpeaking] = useState(initial?.speaking || {});    // role → true while speaking
  const [present, setPresent] = useState(initial?.present || []);       // roles in the room
  const [blocked, setBlocked] = useState(initial?.blocked === true);
  const [error, setError] = useState(null);
  const ringStop = useRef(null);
  const roomRef = useRef(null);
  const els = useRef([]);

  const stopRing = () => {
    const s = ringStop.current;
    ringStop.current = null;
    if (s) s();
  };
  const detach = () => {
    for (const el of els.current) { try { el.remove(); } catch { /* gone */ } }
    els.current = [];
  };

  // The ring starts with the panel, and stops on the first voice, the end, or the cap.
  useEffect(() => {
    if (typeof window === "undefined" || initial) return undefined;
    ringStop.current = startRing();
    const cap = setTimeout(stopRing, RING_MAX_MS);
    return () => { clearTimeout(cap); stopRing(); };
  }, []);

  // Join the call's room once the platform has it.
  useEffect(() => {
    if (!callId || typeof window === "undefined" || initial) return undefined;
    let alive = true;
    const fail = (text) => { if (!alive) return; stopRing(); setState("failed"); setError(text); };
    (async () => {
      const deadline = Date.now() + LISTEN_WAIT_MS;
      let pass = null;
      while (alive && !pass) {
        try {
          pass = await listen(callId);
        } catch (err) {
          const what = listenRefusal(err);
          if (what === "ended") { if (alive) { stopRing(); setState("ended"); } return; }
          if (what === "fail") return fail(err?.body?.message || err?.message || "The platform would not let this screen listen.");
          if (Date.now() > deadline) return fail("The call never reached the voice server, so there is nothing to listen to.");
          await new Promise((r) => setTimeout(r, LISTEN_RETRY_MS));
        }
      }
      if (!alive || !pass) return;
      const { Room, RoomEvent, ParticipantKind } = await import("livekit-client");
      const kinds = ParticipantKind || {};
      const room = new Room({ adaptiveStream: false, dynacast: false });
      roomRef.current = room;
      let far = null;
      const roles = () => {
        far = far || farLegOf([...room.remoteParticipants.values()], kinds);
        const seen = new Set([...room.remoteParticipants.values()].map((p) => roleOf(p, far, kinds)));
        setPresent(["alfred", "far", "other"].filter((r) => seen.has(r)));
      };
      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== "audio") return;
        const el = track.attach();
        el.autoplay = true;
        el.style.display = "none";
        document.body.appendChild(el);
        els.current.push(el);
        // Played explicitly, and a refusal is SAID: `autoplay` alone fails silently.
        const p = el.play?.();
        if (p && typeof p.catch === "function") p.catch(() => setBlocked(true));
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        try { track.detach().forEach((e) => e.remove()); } catch { /* gone */ }
      });
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => setBlocked(!room.canPlaybackAudio));
      room.on(RoomEvent.ParticipantConnected, roles);
      room.on(RoomEvent.ParticipantDisconnected, roles);
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        const now = {};
        for (const sp of speakers || []) if (!sp.isLocal) now[roleOf(sp, far, kinds)] = true;
        setSpeaking(now);
        if (Object.keys(now).length) {
          stopRing();
          setState((s) => (s === "connecting" || s === "ringing" ? "live" : s));
        }
      });
      room.on(RoomEvent.Disconnected, () => {
        stopRing();
        detach();
        if (alive) { setSpeaking({}); setState((s) => (s === "failed" ? s : "ended")); }
      });
      await room.connect(pass.url, pass.token);
      if (!alive) { await room.disconnect().catch(() => {}); return; }
      roles();
      setState((s) => (s === "connecting" ? "ringing" : s));
      try {
        await room.startAudio();
        setBlocked(!room.canPlaybackAudio);
      } catch {
        setBlocked(true);
      }
    })().catch((err) => fail(err?.message || "Could not join the call."));
    return () => {
      alive = false;
      const r = roomRef.current;
      roomRef.current = null;
      if (r) r.disconnect().catch?.(() => {});
      detach();
    };
  }, [callId]);

  // WHAT THE BROWSER IS ACTUALLY RECEIVING, in the console every 5s while it is live: bytes and
  // energy per track. Chips can light up (the server's speaking signal) while no audio arrives, and
  // this is what tells "never arrived" from "arrived and was not played". The Rep Phone's lesson.
  useEffect(() => {
    if (state !== "live" && state !== "ringing") return undefined;
    const t = setInterval(async () => {
      const r = roomRef.current;
      if (!r) return;
      const out = { canPlayback: r.canPlaybackAudio, tracks: [] };
      for (const p of r.remoteParticipants.values()) {
        for (const pub of p.audioTrackPublications.values()) {
          let st = null;
          try { st = await pub.track?.getReceiverStats?.(); } catch { /* not supported */ }
          out.tracks.push({ who: p.identity, subscribed: !!pub.track, bytes: st?.bytesReceived ?? null,
            energy: st?.totalAudioEnergy != null ? Number(st.totalAudioEnergy.toFixed(3)) : null });
        }
      }
      console.log("[freight-listen] rx", JSON.stringify(out));
    }, 5000);
    return () => clearInterval(t);
  }, [state]);

  const unblock = async () => {
    primeAudio();
    const r = roomRef.current;
    try {
      if (r) await r.startAudio();
      for (const el of els.current) await el.play?.().catch(() => {});
      setBlocked(r ? !r.canPlaybackAudio : false);
    } catch {
      setBlocked(true);
    }
  };

  const shown = state === "failed" ? "failed" : blocked && state !== "ended" ? "blocked" : state;
  const chips = (present.length ? present : ["alfred", "far"]);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontFamily: mono, fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.ink3 }}>
          {state === "ended" ? "Was live" : "Live"} · AI persona
        </span>
        <span style={{ fontSize: 13.5, fontWeight: 500, color: T.ink }}>{headline(shown, name)}</span>
      </div>
      {state !== "failed" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {chips.map((role) => (
            <span key={role} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, padding: "3px 10px",
              borderRadius: 999, border: `1px solid ${speaking[role] ? T.ink : T.hair}`, color: speaking[role] ? T.ink : T.ink2 }}>
              <span style={{ width: 7, height: 7, borderRadius: 4, background: speaking[role] ? T.ink : T.ink4,
                transition: "background .12s" }} />
              {labelFor(role, name)}
            </span>
          ))}
        </div>
      )}
      {shown === "blocked" && (
        <button onClick={unblock} style={{ justifySelf: "start", font: "inherit", fontSize: 13, padding: "6px 12px", borderRadius: 6,
          border: `1px solid ${T.ink}`, background: T.ink, color: "#fff", cursor: "pointer" }}>
          Tap to hear the call
        </button>
      )}
      {error && <div style={{ fontSize: 12.5, color: T.ink }}>{error}</div>}
      <div style={{ fontSize: 12, color: T.ink3, lineHeight: 1.45 }}>
        An AI is playing {firstName(name)} for this demonstration, on a 555-01xx number no phone has. Alfred&apos;s side is the
        real call, word for word what a real person would hear.
      </div>
    </div>
  );
}
